import type { ViewStyle } from 'react-native'
import { Dimensions } from 'react-native'
import { PositionAlignment, PositionDirection } from './position-enums'
import type { LayoutInfo } from '~/hooks/layout/types'

export interface PositionStyles {
  container: ViewStyle
  wrapper: ViewStyle
}

interface AlignmentStylesParams {
  align: PositionAlignment
  side: PositionDirection
  contentGap: number
  contentLayout?: LayoutInfo | null
  rootLayout?: LayoutInfo | null
}

const SCREEN_MARGIN = 8

export const getAlignmentStyles = ({
  align,
  side,
  contentGap,
  contentLayout,
  rootLayout,
}: AlignmentStylesParams): ViewStyle => {
  const horizontalAlignmentStyles: Record<PositionAlignment, ViewStyle> = {
    [PositionAlignment.START]: {
      alignSelf: 'flex-end',
      right: (() => {
        const screenWidth = Dimensions.get('window').width
        const MIN_LEFT_MARGIN = 16

        const rightX =
          screenWidth - ((rootLayout?.x || 0) + (rootLayout?.width || 0))
        const centerRoot = rightX + (rootLayout?.width || 0) / 2
        const rightOffset =
          centerRoot + MIN_LEFT_MARGIN - (contentLayout?.width || 0)

        return Math.max(rightOffset, 0)
      })(),
    },
    [PositionAlignment.MIDDLE]: {
      left: (() => {
        const screenWidth = Dimensions.get('window').width
        const contentWidth = contentLayout?.width || 0

        let centeredLeft =
          (rootLayout?.x || 0) +
          (rootLayout?.width || 0) / 2 -
          contentWidth / 2 -
          SCREEN_MARGIN

        const rightEdge = centeredLeft + contentWidth + SCREEN_MARGIN
        if (rightEdge > screenWidth) {
          centeredLeft = screenWidth - contentWidth - SCREEN_MARGIN
        }

        centeredLeft = Math.max(SCREEN_MARGIN, centeredLeft)

        return centeredLeft
      })(),
    },
    [PositionAlignment.END]: {
      left: (() => {
        const MIN_RIGHT_MARGIN = 16

        const centerX = (rootLayout?.x || 0) + (rootLayout?.width || 0) / 2
        const leftSpace =
          centerX + MIN_RIGHT_MARGIN - (contentLayout?.width || 0)

        return Math.max(leftSpace, 0)
      })(),
    },
  }

  const verticalAlignmentStyles: Record<PositionAlignment, ViewStyle> = {
    [PositionAlignment.START]: {
      top: (() => {
        const MIN_TOP_MARGIN = 16
        const ARROW_SIZE = 8

        const middle = (rootLayout?.height || 0) / 2
        const middleContent =
          middle +
          (contentLayout?.height || 0) / 2 -
          MIN_TOP_MARGIN -
          ARROW_SIZE / 2

        return middleContent
      })(),
    },
    [PositionAlignment.MIDDLE]: {
      top: (() => {
        return (rootLayout?.height || 0) / 2
      })(),
    },
    [PositionAlignment.END]: {
      top: (() => {
        const MIN_BOTTOM_MARGIN = 16
        const ARROW_SIZE = 8

        const middle = (rootLayout?.height || 0) / 2
        const middleContent =
          middle -
          (contentLayout?.height || 0) / 2 +
          MIN_BOTTOM_MARGIN +
          ARROW_SIZE / 2

        return middleContent
      })(),
    },
  }

  const isHorizontalAlignment =
    side === PositionDirection.TOP || side === PositionDirection.BOTTOM
  const isVerticalAlignment =
    side === PositionDirection.LEFT || side === PositionDirection.RIGHT

  if (isHorizontalAlignment) {
    return horizontalAlignmentStyles[align] || {}
  }

  if (isVerticalAlignment) {
    return verticalAlignmentStyles[align] || {}
  }

  return {}
}

interface DirectionStylesParams {
  side: PositionDirection
  contentGap: number
  contentLayout?: LayoutInfo | null
  rootLayout?: LayoutInfo | null
}

export const getDirectionStyles = ({
  side,
  contentGap,
  contentLayout,
  rootLayout,
}: DirectionStylesParams): PositionStyles => {
  const ARROW_SIZE = 8

  const directionStylesMap: Record<PositionDirection, PositionStyles> = {
    [PositionDirection.TOP]: {
      container: {},
      wrapper: {
        top: -(contentLayout?.height || 0) - contentGap - ARROW_SIZE,
        flexDirection: 'column-reverse',
        justifyContent: 'flex-end',
      },
    },
    [PositionDirection.BOTTOM]: {
      container: {},
      wrapper: {
        justifyContent: 'flex-end',
        top: contentGap + ARROW_SIZE,
      },
    },
    [PositionDirection.LEFT]: {
      container: {
        maxWidth: (() => {
          const ARROW_SIZE = 8
          const CONTENT_GAP = 8
          const MARGIN_SCREEN = 8

          const maxWidth =
            (rootLayout?.x || 0) - ARROW_SIZE - CONTENT_GAP - MARGIN_SCREEN
          return Math.max(maxWidth, 0)
        })(),
      },
      wrapper: {
        top: -(contentLayout?.height || 0) / 2,
        flexDirection: 'row-reverse',
        justifyContent: 'flex-end',
      },
    },
    [PositionDirection.RIGHT]: {
      container: {
        maxWidth: (() => {
          const screenWidth = Dimensions.get('window').width

          const ARROW_SIZE = 8
          const CONTENT_GAP = 8
          const MARGIN_SCREEN = 8

          const rightX =
            screenWidth - ((rootLayout?.x || 0) + (rootLayout?.width || 0))

          const maxWidth = rightX - ARROW_SIZE - CONTENT_GAP - MARGIN_SCREEN
          return Math.max(maxWidth, 0)
        })(),
      },
      wrapper: {
        top: -(contentLayout?.height || 0) / 2,
        flexDirection: 'row',
        justifyContent: 'flex-end',
      },
    },
  }

  return (
    directionStylesMap[side] || {
      container: {},
      wrapper: {},
    }
  )
}

interface CombinedPositionStylesParams {
  align: PositionAlignment
  side: PositionDirection
  contentGap: number
  rootLayout?: LayoutInfo | null
  contentLayout?: LayoutInfo | null
}

export const getCombinedPositionStyles = ({
  align,
  side,
  contentGap,
  rootLayout,
  contentLayout,
}: CombinedPositionStylesParams): PositionStyles => {
  const directionStyles = getDirectionStyles({
    side,
    contentGap,
    contentLayout,
    rootLayout,
  })
  const alignmentStyles = getAlignmentStyles({
    align,
    side,
    contentGap,
    contentLayout,
    rootLayout,
  })

  return {
    container: {
      ...directionStyles.container,
      ...alignmentStyles,
    },
    wrapper: directionStyles.wrapper,
  }
}
